import React, { useEffect, useRef } from 'react';
import './FluidSection.css';
import { motion } from '../utils/motion';

const FluidSection = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        let destroyed = false;
        let rafHandle = 0;

        function fluidSimulation(canvasElement) {
            canvasElement.width = canvasElement.clientWidth;
            canvasElement.height = canvasElement.clientHeight;

            const config = {
                SIM_RESOLUTION: 128,
                DYE_RESOLUTION: 512,
                DENSITY_DISSIPATION: 0.97,
                VELOCITY_DISSIPATION: 0.96,
                PRESSURE_DISSIPATION: 0.8,
                PRESSURE_ITERATIONS: 20,
                CURL: 35,
                SPLAT_RADIUS: 0.25,
            };

            function Pointer() {
                this.x = 0;
                this.y = 0;
                this.dx = 0;
                this.dy = 0;
                this.moved = false;
                this.color = { r: 0, g: 0, b: 0 };
            }

            let pointers = [new Pointer()];

            const params = { alpha: true, depth: false, stencil: false, antialias: false, preserveDrawingBuffer: false };
            let gl = canvasElement.getContext('webgl2', params) || canvasElement.getContext('webgl', params) || canvasElement.getContext('experimental-webgl', params);
            if (!gl) return null;

            const isWebGL2 = !!gl.createRenderbuffer;
            let halfFloat = isWebGL2 ? null : gl.getExtension('OES_texture_half_float');
            let supportLinearFiltering = isWebGL2
                ? gl.getExtension('OES_texture_float_linear')
                : gl.getExtension('OES_texture_half_float_linear');

            if (isWebGL2) gl.getExtension('EXT_color_buffer_float');

            const halfFloatTexType = isWebGL2 ? gl.HALF_FLOAT : (halfFloat ? halfFloat.HALF_FLOAT_OES : gl.FLOAT);

            function getSupportedFormat(glCtx, internalFormat, format, type) {
                if (!supportRenderTextureFormat(glCtx, internalFormat, format, type)) {
                    switch (internalFormat) {
                        case glCtx.R16F: return getSupportedFormat(glCtx, glCtx.RG16F, glCtx.RG, type);
                        case glCtx.RG16F: return getSupportedFormat(glCtx, glCtx.RGBA16F, glCtx.RGBA, type);
                        default: return null;
                    }
                }
                return { internalFormat, format };
            }

            function supportRenderTextureFormat(glCtx, internalFormat, format, type) {
                let texture = glCtx.createTexture();
                glCtx.bindTexture(glCtx.TEXTURE_2D, texture);
                glCtx.texParameteri(glCtx.TEXTURE_2D, glCtx.TEXTURE_MIN_FILTER, glCtx.NEAREST);
                glCtx.texParameteri(glCtx.TEXTURE_2D, glCtx.TEXTURE_MAG_FILTER, glCtx.NEAREST);
                glCtx.texParameteri(glCtx.TEXTURE_2D, glCtx.TEXTURE_WRAP_S, glCtx.CLAMP_TO_EDGE);
                glCtx.texParameteri(glCtx.TEXTURE_2D, glCtx.TEXTURE_WRAP_T, glCtx.CLAMP_TO_EDGE);
                glCtx.texImage2D(glCtx.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);

                let fbo = glCtx.createFramebuffer();
                glCtx.bindFramebuffer(glCtx.FRAMEBUFFER, fbo);
                glCtx.framebufferTexture2D(glCtx.FRAMEBUFFER, glCtx.COLOR_ATTACHMENT0, glCtx.TEXTURE_2D, texture, 0);

                return glCtx.checkFramebufferStatus(glCtx.FRAMEBUFFER) === glCtx.FRAMEBUFFER_COMPLETE;
            }

            const formatRGBA = isWebGL2 ? getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, halfFloatTexType) : getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
            const formatRG = isWebGL2 ? getSupportedFormat(gl, gl.RG16F, gl.RG, halfFloatTexType) : formatRGBA;
            const formatR = isWebGL2 ? getSupportedFormat(gl, gl.R16F, gl.RED, halfFloatTexType) : formatRGBA;

            const ext = { formatRGBA, formatRG, formatR, halfFloatTexType, supportLinearFiltering };

            class GLProgram {
                constructor(vertexShader, fragmentShader) {
                    this.uniforms = {};
                    this.program = gl.createProgram();
                    gl.attachShader(this.program, vertexShader);
                    gl.attachShader(this.program, fragmentShader);
                    gl.linkProgram(this.program);
                    const uniformCount = gl.getProgramParameter(this.program, gl.ACTIVE_UNIFORMS);
                    for (let i = 0; i < uniformCount; i++) {
                        const name = gl.getActiveUniform(this.program, i).name;
                        this.uniforms[name] = gl.getUniformLocation(this.program, name);
                    }
                }
                bind() { gl.useProgram(this.program); }
            }

            function compileShader(type, source) {
                const shader = gl.createShader(type);
                gl.shaderSource(shader, source);
                gl.compileShader(shader);
                return shader;
            }

            const baseVertexShader = compileShader(gl.VERTEX_SHADER, `
                precision highp float;
                attribute vec2 aPosition;
                varying vec2 vUv;
                varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
                uniform vec2 texelSize;
                void main () {
                    vUv = aPosition * 0.5 + 0.5;
                    vL = vUv - vec2(texelSize.x, 0.0);
                    vR = vUv + vec2(texelSize.x, 0.0);
                    vT = vUv + vec2(0.0, texelSize.y);
                    vB = vUv - vec2(0.0, texelSize.y);
                    gl_Position = vec4(aPosition, 0.0, 1.0);
                }
            `);

            const clearShader = compileShader(gl.FRAGMENT_SHADER, `
                precision mediump float;
                varying highp vec2 vUv;
                uniform sampler2D uTexture;
                uniform float value;
                void main () { gl_FragColor = value * texture2D(uTexture, vUv); }
            `);

            const displayShader = compileShader(gl.FRAGMENT_SHADER, `
                precision highp float;
                varying vec2 vUv;
                uniform sampler2D uTexture;
                void main () {
                    vec3 C = texture2D(uTexture, vUv).rgb;
                    float a = max(C.r, max(C.g, C.b));
                    gl_FragColor = vec4(C, a);
                }
            `);

            const splatShader = compileShader(gl.FRAGMENT_SHADER, `
                precision highp float;
                varying vec2 vUv;
                uniform sampler2D uTarget;
                uniform float aspectRatio;
                uniform vec3 color;
                uniform vec2 point;
                uniform float radius;
                void main () {
                    vec2 p = vUv - point.xy;
                    p.x *= aspectRatio;
                    vec3 splat = exp(-dot(p, p) / radius) * color;
                    vec3 base = texture2D(uTarget, vUv).xyz;
                    gl_FragColor = vec4(base + splat, 1.0);
                }
            `);

            const advectionShader = compileShader(gl.FRAGMENT_SHADER, `
                precision highp float;
                varying vec2 vUv;
                uniform sampler2D uVelocity;
                uniform sampler2D uSource;
                uniform vec2 texelSize;
                uniform float dt;
                uniform float dissipation;
                void main () {
                    vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
                    gl_FragColor = dissipation * texture2D(uSource, coord);
                    gl_FragColor.a = 1.0;
                }
            `);

            const divergenceShader = compileShader(gl.FRAGMENT_SHADER, `
                precision mediump float;
                varying highp vec2 vUv;
                varying highp vec2 vL; varying highp vec2 vR; varying highp vec2 vT; varying highp vec2 vB;
                uniform sampler2D uVelocity;
                void main () {
                    float L = texture2D(uVelocity, vL).x;
                    float R = texture2D(uVelocity, vR).x;
                    float T = texture2D(uVelocity, vT).y;
                    float B = texture2D(uVelocity, vB).y;
                    vec2 C = texture2D(uVelocity, vUv).xy;
                    if (vL.x < 0.0) { L = -C.x; }
                    if (vR.x > 1.0) { R = -C.x; }
                    if (vT.y > 1.0) { T = -C.y; }
                    if (vB.y < 0.0) { B = -C.y; }
                    float div = 0.5 * (R - L + T - B);
                    gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
                }
            `);

            const curlShader = compileShader(gl.FRAGMENT_SHADER, `
                precision mediump float;
                varying highp vec2 vUv;
                varying highp vec2 vL; varying highp vec2 vR; varying highp vec2 vT; varying highp vec2 vB;
                uniform sampler2D uVelocity;
                void main () {
                    float L = texture2D(uVelocity, vL).y;
                    float R = texture2D(uVelocity, vR).y;
                    float T = texture2D(uVelocity, vT).x;
                    float B = texture2D(uVelocity, vB).x;
                    float vorticity = R - L - T + B;
                    gl_FragColor = vec4(0.5 * vorticity, 0.0, 0.0, 1.0);
                }
            `);

            const vorticityShader = compileShader(gl.FRAGMENT_SHADER, `
                precision highp float;
                varying vec2 vUv;
                varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
                uniform sampler2D uVelocity;
                uniform sampler2D uCurl;
                uniform float curl;
                uniform float dt;
                void main () {
                    float L = texture2D(uCurl, vL).x;
                    float R = texture2D(uCurl, vR).x;
                    float T = texture2D(uCurl, vT).x;
                    float B = texture2D(uCurl, vB).x;
                    float C = texture2D(uCurl, vUv).x;
                    vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
                    force /= length(force) + 0.0001;
                    force *= curl * C;
                    force.y *= -1.0;
                    vec2 vel = texture2D(uVelocity, vUv).xy;
                    gl_FragColor = vec4(vel + force * dt, 0.0, 1.0);
                }
            `);

            const pressureShader = compileShader(gl.FRAGMENT_SHADER, `
                precision mediump float;
                varying highp vec2 vUv;
                varying highp vec2 vL; varying highp vec2 vR; varying highp vec2 vT; varying highp vec2 vB;
                uniform sampler2D uPressure;
                uniform sampler2D uDivergence;
                void main () {
                    float L = texture2D(uPressure, vL).x;
                    float R = texture2D(uPressure, vR).x;
                    float T = texture2D(uPressure, vT).x;
                    float B = texture2D(uPressure, vB).x;
                    float divergence = texture2D(uDivergence, vUv).x;
                    float pressure = (L + R + B + T - divergence) * 0.25;
                    gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
                }
            `);

            const gradientSubtractShader = compileShader(gl.FRAGMENT_SHADER, `
                precision mediump float;
                varying highp vec2 vUv;
                varying highp vec2 vL; varying highp vec2 vR; varying highp vec2 vT; varying highp vec2 vB;
                uniform sampler2D uPressure;
                uniform sampler2D uVelocity;
                void main () {
                    float L = texture2D(uPressure, vL).x;
                    float R = texture2D(uPressure, vR).x;
                    float T = texture2D(uPressure, vT).x;
                    float B = texture2D(uPressure, vB).x;
                    vec2 velocity = texture2D(uVelocity, vUv).xy;
                    velocity.xy -= vec2(R - L, T - B);
                    gl_FragColor = vec4(velocity, 0.0, 1.0);
                }
            `);

            const blit = (() => {
                gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
                gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
                gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
                gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
                gl.enableVertexAttribArray(0);
                return (destination) => {
                    gl.bindFramebuffer(gl.FRAMEBUFFER, destination);
                    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
                };
            })();

            let simWidth, simHeight, dyeWidth, dyeHeight;
            let density, velocity, divergence, curl, pressure;

            const clearProgram = new GLProgram(baseVertexShader, clearShader);
            const displayProgram = new GLProgram(baseVertexShader, displayShader);
            const splatProgram = new GLProgram(baseVertexShader, splatShader);
            const advectionProgram = new GLProgram(baseVertexShader, advectionShader);
            const divergenceProgram = new GLProgram(baseVertexShader, divergenceShader);
            const curlProgram = new GLProgram(baseVertexShader, curlShader);
            const vorticityProgram = new GLProgram(baseVertexShader, vorticityShader);
            const pressureProgram = new GLProgram(baseVertexShader, pressureShader);
            const gradienSubtractProgram = new GLProgram(baseVertexShader, gradientSubtractShader);

            function createFBO(w, h, internalFormat, format, type, param) {
                gl.activeTexture(gl.TEXTURE0);
                let texture = gl.createTexture();
                gl.bindTexture(gl.TEXTURE_2D, texture);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null);
                let fbo = gl.createFramebuffer();
                gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
                gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
                gl.viewport(0, 0, w, h);
                gl.clear(gl.COLOR_BUFFER_BIT);
                return { texture, fbo, width: w, height: h, attach(id) { gl.activeTexture(gl.TEXTURE0 + id); gl.bindTexture(gl.TEXTURE_2D, texture); return id; } };
            }

            function createDoubleFBO(w, h, internalFormat, format, type, param) {
                let fbo1 = createFBO(w, h, internalFormat, format, type, param);
                let fbo2 = createFBO(w, h, internalFormat, format, type, param);
                return {
                    get read() { return fbo1; }, set read(value) { fbo1 = value; },
                    get write() { return fbo2; }, set write(value) { fbo2 = value; },
                    swap() { let temp = fbo1; fbo1 = fbo2; fbo2 = temp; }
                };
            }

            function getResolution(resolution) {
                let aspectRatio = gl.drawingBufferWidth / gl.drawingBufferHeight;
                if (aspectRatio < 1) aspectRatio = 1.0 / aspectRatio;
                let max = Math.round(resolution * aspectRatio);
                let min = Math.round(resolution);
                return gl.drawingBufferWidth > gl.drawingBufferHeight ? { width: max, height: min } : { width: min, height: max };
            }

            function initFramebuffers() {
                let simRes = getResolution(config.SIM_RESOLUTION);
                let dyeRes = getResolution(config.DYE_RESOLUTION);
                simWidth = simRes.width; simHeight = simRes.height;
                dyeWidth = dyeRes.width; dyeHeight = dyeRes.height;

                const texType = ext.halfFloatTexType;
                const rgba = ext.formatRGBA; const rg = ext.formatRG; const r = ext.formatR;
                const filtering = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST;

                density = createDoubleFBO(dyeWidth, dyeHeight, rgba.internalFormat, rgba.format, texType, filtering);
                velocity = createDoubleFBO(simWidth, simHeight, rg.internalFormat, rg.format, texType, filtering);
                divergence = createFBO(simWidth, simHeight, r.internalFormat, r.format, texType, gl.NEAREST);
                curl = createFBO(simWidth, simHeight, r.internalFormat, r.format, texType, gl.NEAREST);
                pressure = createDoubleFBO(simWidth, simHeight, r.internalFormat, r.format, texType, gl.NEAREST);
            }

            initFramebuffers();
            multipleSplats(25);

            let orbitAngle = 0;
            let vPrevX = 0, vPrevY = 0;
            let virtualColor = null;
            let lastVColorTime = 0;

            function generateColor() {
                const hues = [0.55, 0.72, 0.9, 0.48];
                const h = hues[Math.floor(Math.random() * hues.length)];
                let c = HSVtoRGB(h, 1.0, 1.0);
                c.r *= 0.35; c.g *= 0.35; c.b *= 0.35;
                return c;
            }

            function HSVtoRGB(h, s, v) {
                let r, g, b, i, f, p, q, t;
                i = Math.floor(h * 6); f = h * 6 - i;
                p = v * (1 - s); q = v * (1 - f * s); t = v * (1 - (1 - f) * s);
                switch (i % 6) {
                    case 0: r = v; g = t; b = p; break;
                    case 1: r = q; g = v; b = p; break;
                    case 2: r = p; g = v; b = t; break;
                    case 3: r = p; g = q; b = v; break;
                    case 4: r = t; g = p; b = v; break;
                    case 5: r = v; g = p; b = q; break;
                    default: r = 0; g = 0; b = 0; break;
                }
                return { r, g, b };
            }

            function splat(x, y, dx, dy, color) {
                gl.viewport(0, 0, simWidth, simHeight);
                splatProgram.bind();
                gl.uniform1i(splatProgram.uniforms.uTarget, velocity.read.attach(0));
                gl.uniform1f(splatProgram.uniforms.aspectRatio, canvasElement.width / canvasElement.height);
                gl.uniform2f(splatProgram.uniforms.point, x / canvasElement.width, 1.0 - y / canvasElement.height);
                gl.uniform3f(splatProgram.uniforms.color, dx, -dy, 1.0);
                gl.uniform1f(splatProgram.uniforms.radius, config.SPLAT_RADIUS / 100.0);
                blit(velocity.write.fbo);
                velocity.swap();

                gl.viewport(0, 0, dyeWidth, dyeHeight);
                gl.uniform1i(splatProgram.uniforms.uTarget, density.read.attach(0));
                gl.uniform3f(splatProgram.uniforms.color, color.r, color.g, color.b);
                blit(density.write.fbo);
                density.swap();
            }

            function multipleSplats(amount) {
                for (let i = 0; i < amount; i++) {
                    const color = generateColor();
                    color.r *= 8.0; color.g *= 8.0; color.b *= 8.0;
                    const x = canvasElement.width * Math.random();
                    const y = canvasElement.height * Math.random();
                    const dx = 800 * (Math.random() - 0.5);
                    const dy = 800 * (Math.random() - 0.5);
                    splat(x, y, dx, dy, color);
                }
            }

            function driveVirtualPointer() {
                const cx = canvasElement.width / 2;
                const cy = canvasElement.height / 2;
                const r = Math.min(canvasElement.width, canvasElement.height) * 0.3 * (0.8 + 0.2 * Math.sin(orbitAngle * 0.4));
                orbitAngle += 0.02;
                const x = cx + Math.cos(orbitAngle) * r;
                const y = cy + Math.sin(orbitAngle) * r;
                if (!virtualColor || Date.now() - lastVColorTime > 150) {
                    virtualColor = generateColor();
                    virtualColor.r *= 3.5; virtualColor.g *= 3.5; virtualColor.b *= 3.5;
                    lastVColorTime = Date.now();
                }
                const dx = (x - vPrevX) * 6.0;
                const dy = (y - vPrevY) * 6.0;
                vPrevX = x; vPrevY = y;
                splat(x, y, dx, dy, virtualColor);
            }

            function step(dt) {
                gl.disable(gl.BLEND);
                gl.viewport(0, 0, simWidth, simHeight);
                curlProgram.bind();
                gl.uniform2f(curlProgram.uniforms.texelSize, 1.0 / simWidth, 1.0 / simHeight);
                gl.uniform1i(curlProgram.uniforms.uVelocity, velocity.read.attach(0));
                blit(curl.fbo);

                vorticityProgram.bind();
                gl.uniform2f(vorticityProgram.uniforms.texelSize, 1.0 / simWidth, 1.0 / simHeight);
                gl.uniform1i(vorticityProgram.uniforms.uVelocity, velocity.read.attach(0));
                gl.uniform1i(vorticityProgram.uniforms.uCurl, curl.attach(1));
                gl.uniform1f(vorticityProgram.uniforms.curl, config.CURL);
                gl.uniform1f(vorticityProgram.uniforms.dt, dt);
                blit(velocity.write.fbo);
                velocity.swap();

                divergenceProgram.bind();
                gl.uniform2f(divergenceProgram.uniforms.texelSize, 1.0 / simWidth, 1.0 / simHeight);
                gl.uniform1i(divergenceProgram.uniforms.uVelocity, velocity.read.attach(0));
                blit(divergence.fbo);

                clearProgram.bind();
                gl.uniform1i(clearProgram.uniforms.uTexture, pressure.read.attach(0));
                gl.uniform1f(clearProgram.uniforms.value, config.PRESSURE_DISSIPATION);
                blit(pressure.write.fbo);
                pressure.swap();

                pressureProgram.bind();
                gl.uniform2f(pressureProgram.uniforms.texelSize, 1.0 / simWidth, 1.0 / simHeight);
                gl.uniform1i(pressureProgram.uniforms.uDivergence, divergence.attach(0));
                for (let i = 0; i < config.PRESSURE_ITERATIONS; i++) {
                    gl.uniform1i(pressureProgram.uniforms.uPressure, pressure.read.attach(1));
                    blit(pressure.write.fbo);
                    pressure.swap();
                }

                gradienSubtractProgram.bind();
                gl.uniform2f(gradienSubtractProgram.uniforms.texelSize, 1.0 / simWidth, 1.0 / simHeight);
                gl.uniform1i(gradienSubtractProgram.uniforms.uPressure, pressure.read.attach(0));
                gl.uniform1i(gradienSubtractProgram.uniforms.uVelocity, velocity.read.attach(1));
                blit(velocity.write.fbo);
                velocity.swap();

                advectionProgram.bind();
                gl.uniform2f(advectionProgram.uniforms.texelSize, 1.0 / simWidth, 1.0 / simHeight);
                let velocityId = velocity.read.attach(0);
                gl.uniform1i(advectionProgram.uniforms.uVelocity, velocityId);
                gl.uniform1i(advectionProgram.uniforms.uSource, velocityId);
                gl.uniform1f(advectionProgram.uniforms.dt, dt);
                gl.uniform1f(advectionProgram.uniforms.dissipation, config.VELOCITY_DISSIPATION);
                blit(velocity.write.fbo);
                velocity.swap();

                gl.viewport(0, 0, dyeWidth, dyeHeight);
                gl.uniform1i(advectionProgram.uniforms.uVelocity, velocity.read.attach(0));
                gl.uniform1i(advectionProgram.uniforms.uSource, density.read.attach(1));
                gl.uniform1f(advectionProgram.uniforms.dissipation, config.DENSITY_DISSIPATION);
                blit(density.write.fbo);
                density.swap();
            }

            function render() {
                gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
                gl.enable(gl.BLEND);
                gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
                displayProgram.bind();
                gl.uniform1i(displayProgram.uniforms.uTexture, density.read.attach(0));
                blit(null);
            }

            function update() {
                if (destroyed) return;
                if (canvasElement.width !== canvasElement.clientWidth || canvasElement.height !== canvasElement.clientHeight) {
                    canvasElement.width = canvasElement.clientWidth; canvasElement.height = canvasElement.clientHeight;
                    initFramebuffers();
                }
                driveVirtualPointer();
                for (let i = 0; i < pointers.length; i++) {
                    const p = pointers[i];
                    if (p.moved) {
                        splat(p.x, p.y, p.dx, p.dy, p.color);
                        p.moved = false;
                    }
                }
                step(0.016);
                render();
                rafHandle = requestAnimationFrame(update);
            }

            const handlePointerMove = (e) => {
                const rect = canvasElement.getBoundingClientRect();
                const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
                const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
                const x = clientX - rect.left;
                const y = clientY - rect.top;
                let pointer = pointers[0];
                pointer.dx = (x - pointer.x) * 6.0;
                pointer.dy = (y - pointer.y) * 6.0;
                pointer.x = x;
                pointer.y = y;
                pointer.color = generateColor();
                pointer.color.r *= 4.0; pointer.color.g *= 4.0; pointer.color.b *= 4.0;
                pointer.moved = true;
            };

            const container = canvasElement.parentElement;
            if (container) {
                container.addEventListener('mousemove', handlePointerMove);
                container.addEventListener('touchmove', handlePointerMove);
            }

            update();

            return () => {
                destroyed = true;
                if (rafHandle) cancelAnimationFrame(rafHandle);
                if (container) {
                    container.removeEventListener('mousemove', handlePointerMove);
                    container.removeEventListener('touchmove', handlePointerMove);
                }
            };
        }

        const cleanup = fluidSimulation(canvas);
        return () => { if (cleanup) cleanup(); };
    }, []);

    return (
        <section className="fluid-section" id="fluid-experience">
            <div className="container">
                <div className="fluid-section__card glass-card">
                    <canvas ref={canvasRef} className="fluid-canvas" />
                    
                    <div className="fluid-section__overlay">
                        <motion.div 
                            className="fluid-section__badge"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="fluid-section__badge-dot" />
                            <span>✦ Kinetic Visual Lab</span>
                        </motion.div>

                        <motion.h2 
                            className="fluid-section__title"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                        >
                            Experience <span className="fluid-gradient-text">Life Be Like</span> Energy
                        </motion.h2>

                        <motion.p 
                            className="fluid-section__subtitle"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Move your cursor across the canvas to sculpt real-time WebGL fluid swirls, 
                            cyan particle currents, and vibrant neon aura streams.
                        </motion.p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FluidSection;
